import React from "react";
import { Alert, CssBaseline, Divider, Drawer, Icon, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Tooltip, Typography, useMediaQuery, Box, InputBase, Avatar, Badge } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import UserMenu from "./UserMenu";
import UserProfileHeader from "./UserProfileHeader";
import { AuthContext } from "../../contexto";
import { theme } from "../../tema/theme";
import BreadcrumbsDynamic from "../../componentes/BreadcrumbsDynamic";
import useAuthContext from "../../modules/auth/hook/useAuthContext";

// Definimos los dos anchos posibles para el Drawer en Desktop
const DRAWER_FULL_WIDTH = 260;
const DRAWER_COLLAPSED_WIDTH = 70;

const UI = {
  appBg: "#f4f6f9",
  drawerBg: "#ffffff",
  border: "#e9ecef",

  text: "#333333",
  textMuted: "#8a92a6",

  hover: "#f5f6fa",
  activeBg: "#F0F3FB",
  activeText: "#585858",

  subItemText: "#4f5d75",
  subItemActiveBg: "#f0f4fe",
};

const Nav = () => {
  const { navigation, selectedNav, setSelectedNav } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // NUEVO: Estado para controlar si el menú está colapsado en Desktop
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [openModuleId, setOpenModuleId] = React.useState<string | number | null>(selectedNav?.moduloId ?? null);

  React.useEffect(() => {
    if (selectedNav?.moduloId != null) setOpenModuleId(selectedNav.moduloId);
  }, [selectedNav?.moduloId]);

  const toggleDrawer = () => setMobileOpen((v) => !v);

  // NUEVO: Handler para alternar el colapso del menú
  const toggleCollapse = () => setIsCollapsed((v) => !v);

  const onSelectModule = (nav: any) => {
    setSelectedNav(nav);
    // Si el menú está colapsado y hacen clic en un módulo, lo expandimos para que puedan ver las opciones
    if (isCollapsed) {
      setIsCollapsed(false);
      setOpenModuleId(nav.moduloId);
    } else {
      setOpenModuleId((prev) => (prev === nav.moduloId ? null : nav.moduloId));
    }
  };

  const onSelectSub = (path: string) => {
    navigate(path);
    if (!isDesktop) setMobileOpen(false);
  };

  // El ancho actual del drawer dependerá de si está colapsado o no (solo aplica en desktop)
  const currentDrawerWidth = isDesktop
    ? (isCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_FULL_WIDTH)
    : DRAWER_FULL_WIDTH;

  // --- COMPONENTE DE LA BARRA SUPERIOR (TOP BAR) ---
  const TopBar = (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      height: 70,
      px: 3,
      backgroundColor: "#ffffff",
      borderBottom: `1px solid ${UI.border}`,
      position: "sticky",
      top: 0,
      zIndex: 1100,
      width: "100%",
    }}>

      {/* NUEVO: Botón Hamburguesa de Cliniva para encoger/expandir el menú (Solo Desktop) */}
      {isDesktop && (
        <IconButton onClick={toggleCollapse} sx={{ color: UI.textMuted, mr: 2 }}>
          <Icon>menu</Icon>
        </IconButton>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Barra de Búsqueda Global */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#f4f6f9",
        borderRadius: "30px",
        px: 2,
        py: 0.5,
        width: { xs: "150px", sm: "250px", md: "320px" },
        mr: 3,
        transition: "all 0.3s ease",
        "&:focus-within": {
          backgroundColor: "#ffffff",
          boxShadow: "0 0 0 2px #3b71ca"
        }
      }}>
        <Icon sx={{ color: UI.textMuted, fontSize: "20px", mr: 1 }}>search</Icon>
        <InputBase placeholder="Buscar..." sx={{ fontSize: 14, width: "100%", color: UI.text }} />
      </Box>

      {/* Iconos de Acción Derecha */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton size="small" sx={{ color: UI.textMuted }}>
          <Icon>language</Icon>
        </IconButton>

        <IconButton size="small" sx={{ color: UI.textMuted }}>
          <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}>
            <Icon>notifications</Icon>
          </Badge>
        </IconButton>

        <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5, height: 24 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0.5 }}>
          <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "right" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.text, lineHeight: 1.2 }}>
              Ella Jones
            </Typography>
            <Typography sx={{ fontSize: 11, color: UI.textMuted }}>
              Admin
            </Typography>
          </Box>
          <Avatar
            alt="User Avatar"
            src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100"
            sx={{ width: 36, height: 36, border: "2px solid #edf2ff" }}
          />
        </Box>
      </Box>
    </Box>
  );

  // --- CONTENIDO INTERNO DEL DRAWER ---
  const DrawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: UI.drawerBg, overflow: "hidden" }}>

      {/* Header Logotipo (Oculta el texto si está colapsado) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: 70,
          px: isCollapsed ? 0 : 2.5,
          justifyContent: "center",
          overflow: "hidden",
          marginBottom: '10px',
          width: "100%",
          position: "relative"
        }}
      >

        <Link
          to={'/app/pacientes'}
          style={{
            textDecoration: 'none',
            display: "flex",
            justifyContent: "center",
            width: "100%", // Obliga al enlace a ocupar toda la anchura del drawer
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "center",
              width: isCollapsed ? "auto" : "100%",
              mx: isCollapsed ? "auto" : 0, // Al usar auto a los lados, se centra obligatoriamente en su espacio asignado
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 34, // Fijamos un ancho contenedor para los dos círculos encimados
                height: 20,
                position: "relative"
              }}
            >
              <Box sx={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: "#ca4d8c", position: "absolute", left: 0, zIndex: 2 }} />
              <Box sx={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: "#7c5ec2", position: "absolute", left: 18, zIndex: 1 }} />
            </Box>

            {/* Ocultamos el texto si está colapsado */}
            {!isCollapsed && (
              <Typography sx={{ color: "#1a1c1e", fontWeight: 600, fontSize: 19, letterSpacing: "0.2em", lineHeight: 1, userSelect: "none" }}>
                VITAL
              </Typography>
            )}
          </Box>
        </Link>
      </Box>

      {/* 2. NUEVO COMPONENTE: Perfil del usuario sobre el menú */}
      <UserProfileHeader isCollapsed={isCollapsed} />

      <Divider sx={{ borderColor: UI.border, mx: isCollapsed ? 1 : 2.5, mb: 1 }} />

      {/* Menús + submenús */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: isCollapsed ? 1 : 2, py: 1 }}>
        <List disablePadding>
          {navigation.map((nav: any) => {
            const moduleSelected = selectedNav?.moduloId === nav.moduloId;
            const moduleOpen = openModuleId === nav.moduloId;

            // Envolvemos el botón en un Tooltip sólo cuando el menú esté colapsado
            const buttonContent = (
              <ListItemButton
                onClick={() => onSelectModule(nav)}
                sx={{
                  borderRadius: "10px",
                  color: moduleSelected ? UI.activeText : UI.text,
                  px: isCollapsed ? 0 : 0.5,
                  py: 1.2,
                  justifyContent: isCollapsed ? "center" : "initial",
                  backgroundColor: moduleSelected ? UI.activeBg : "transparent",
                  "&:hover": { backgroundColor: UI.hover }
                }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 35, mr: isCollapsed ? 0 : 'initial', justifyContent: "center", color: moduleSelected ? UI.activeText : UI.textMuted }}>
                  <Icon fontSize="small" sx={{ fontSize: "20px" }}>{nav.icono}</Icon>
                </ListItemIcon>

                {/* Ocultar texto si está colapsado */}
                {!isCollapsed && (
                  <ListItemText
                    primary={nav.nombre}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: 13.5,
                          fontWeight: moduleSelected ? 600 : 500,
                          color: moduleSelected ? UI.activeText : UI.text,
                        },
                      },
                    }}
                  />)}

                {!isCollapsed && (moduleOpen ? <ExpandLess sx={{ color: moduleSelected ? UI.activeText : UI.textMuted }} /> : <ExpandMore sx={{ color: moduleSelected ? UI.activeText : UI.textMuted }} />)}
              </ListItemButton>
            );

            return (
              <Box key={nav.moduloId} sx={{ mb: 0.5 }}>
                {isCollapsed ? (
                  <Tooltip title={nav.nombre} placement="right">
                    {buttonContent}
                  </Tooltip>
                ) : buttonContent}

                {/* Submenús colapsables (sólo se muestran si no está colapsado globalmente) */}
                {!isCollapsed && (
                  <Collapse in={moduleOpen} timeout="auto" unmountOnExit>
                    {/* Aumentamos pl: 3.5 para empujar todo el submenú más hacia la derecha */}
                    <List disablePadding sx={{ mt: 0.5, pl: 2.0 }}>
                      {nav.vistas.map((sub: any) => {
                        const active = location.pathname === sub.pathRelativo;
                        return (
                          <ListItemButton
                            key={sub.pathRelativo}
                            onClick={() => onSelectSub(sub.pathRelativo)}
                            sx={{
                              borderRadius: "8px",
                              py: 0.9,
                              px: 1.5,
                              my: 0.25,
                              position: 'relative', // Permite posicionar la flecha de forma controlada
                              color: active ? UI.activeText : UI.subItemText,
                              backgroundColor: "transparent",
                              "&:hover": { backgroundColor: UI.hover },
                              transition: "all 0.2s ease",
                            }}
                          >
                            {/* Flecha indicadora: solo aparece a la izquierda si está seleccionado */}
                            {active && (
                              <Icon
                                sx={{
                                  fontSize: '16px !important',
                                  position: 'absolute',
                                  left: '10px', // Se asoma justo a la izquierda del botón de submenú
                                  color: UI.activeText,
                                  animation: 'fadeInLeft 0.2s ease' // Pequeña transición visual
                                }}
                              >
                                chevron_right
                              </Icon>
                            )}

                            <ListItemText
                              primary={sub.nombre}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontSize: 13,
                                    marginLeft: '20px',
                                    fontWeight: active ? 600 : 400,
                                    // Si está activo, le añade un extra de espacio al texto para no chocar con la flecha
                                    pl: active ? 0.5 : 0,
                                    transition: "padding 0.2s ease"
                                  }
                                }
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ borderColor: UI.border, mx: isCollapsed ? 0.5 : 2 }} />
      {/* Footer user menu (se adapta visualmente si se encoge) */}
      <Box sx={{ p: isCollapsed ? 1 : 2, backgroundColor: UI.drawerBg, display: "flex", justifyContent: "center" }}>
        {isCollapsed ? (
          <Tooltip title="Perfil / Salir" placement="right">
            <IconButton onClick={() => navigate('/app/perfil')} sx={{ color: UI.textMuted }}>
              <Icon>account_circle</Icon>
            </IconButton>
          </Tooltip>
        ) : (
          <UserMenu onLogout={() => setSelectedNav(undefined)} />
        )}
      </Box>
    </Box>
  );

  return (
    <div style={{ display: "flex", backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Hamburguesa Mobile */}
      {!isDesktop && (
        <IconButton
          onClick={toggleDrawer}
          sx={{ position: "fixed", top: 18, left: 12, zIndex: 1200, bgcolor: "#ffffff", border: `1px solid ${UI.border}`, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", "&:hover": { bgcolor: "#f8f9fa" } }}
          size="small"
        >
          <Icon fontSize="small">menu</Icon>
        </IconButton>
      )}

      {/* Drawer Desktop con animaciones CSS fluidas para encogerse */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          anchor="left"
          open
          sx={{
            width: currentDrawerWidth,
            flexShrink: 0,
            transition: "width 0.2s ease-in-out", // Transición fluida
            "& .MuiDrawer-paper": {
              width: currentDrawerWidth,
              boxSizing: "border-box",
              backgroundColor: UI.drawerBg,
              borderRight: `1px solid #FFFFFF`,
              transition: "width 0.2s ease-in-out", // Transición fluida en el panel interno
              overflowX: "hidden",
            }
          }}
        >
          {DrawerContent}
        </Drawer>
      )}

      {/* Drawer Mobile */}
      {!isDesktop && (
        <Drawer variant="temporary" anchor="left" open={mobileOpen} onClose={toggleDrawer} ModalProps={{ keepMounted: true }} sx={{ "& .MuiDrawer-paper": { width: currentDrawerWidth, backgroundColor: UI.drawerBg } }}>
          {DrawerContent}
        </Drawer>
      )}

      {/* CONTENEDOR PRINCIPAL */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>

        {TopBar}

        {/* 1. Cambiamos overflowY: "auto" por "hidden" para congelar el scroll de la app general.
    2. Lo convertimos en Flexbox vertical para poder calcular el espacio restante del Outlet.
  */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "#F0F3FB"
          }}
        >

          {/* Nombre de la página y Breadcrumb (mantiene su tamaño natural) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: "44px",
              marginBottom: 3,
              flexShrink: 0 // Evita que este encabezado se aplaste si la pantalla es muy pequeña
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#4d4d4d",
                textTransform: "capitalize",
                lineHeight: 1,
                display: "flex",
                alignItems: "center"
              }}
            >
              {selectedNav?.nombre || "Pacientes"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BreadcrumbsDynamic />
            </Box>
          </Box>

          {/* NUEVO ENVOLTORIO:
      Este Box toma EXACTAMENTE todo el alto restante de la pantalla y fuerza
      a que cualquier página renderizada en el <Outlet /> se ajuste a este tamaño.
    */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Outlet />
          </Box>

        </Box>
      </Box>
    </div>
  );
};

export default Nav;